import { useEffect } from 'react';

/**
 * Tiny zero-dependency custom hook that updates document.title and meta tags
 * dynamically inside useEffect. Provides zero-overhead SEO without react-helmet.
 */
export default function usePageSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image'
} = {}) {
  useEffect(() => {
    // 1. Update Title
    if (title) {
      document.title = title;
    }

    // Helper to safely set or create a <meta> tag
    const setMetaTag = (attributeName, attributeValue, content) => {
      if (!content) return;
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // 2. Standard Meta Tags
    if (description) {
      setMetaTag('name', 'description', description);
    }
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    // 3. Open Graph Tags
    const resolvedOgTitle = ogTitle || title;
    if (resolvedOgTitle) {
      setMetaTag('property', 'og:title', resolvedOgTitle);
    }
    const resolvedOgDescription = ogDescription || description;
    if (resolvedOgDescription) {
      setMetaTag('property', 'og:description', resolvedOgDescription);
    }
    if (ogImage) {
      setMetaTag('property', 'og:image', ogImage);
    }
    if (ogUrl) {
      setMetaTag('property', 'og:url', ogUrl);
    }

    // 4. Twitter Card Tags
    if (twitterCard) {
      setMetaTag('name', 'twitter:card', twitterCard);
    }
    if (resolvedOgTitle) {
      setMetaTag('name', 'twitter:title', resolvedOgTitle);
    }
    if (resolvedOgDescription) {
      setMetaTag('name', 'twitter:description', resolvedOgDescription);
    }
    if (ogImage) {
      setMetaTag('name', 'twitter:image', ogImage);
    }

    // 5. Canonical Link
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', canonical);
    }
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, twitterCard]);
}
